import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { t } from "i18next";
import {
  compact,
  debounce,
  get,
  isEmpty,
  isNull,
  toNumber,
  uniqBy,
} from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { RepportType } from "../../pages/settings/notifications";
import { THorse } from "../../shared/types/horse.type";
import { TSchedule } from "../../shared/types/schedule.type";
import { Ttrack } from "../../shared/types/tracks.type";
import { TTrainerHorse } from "../../shared/types/trainerHorse.type";
import { TTrainingType } from "../../shared/types/training.type";
import { formatMetricIntoImperialDistance } from "../../shared/utils/conversion";
import ListSkeleton from "../exams/loader/ListSkeleton";

dayjs.extend(utc);
dayjs.extend(timezone);

type NewChannelStep1Props = {
  step: number;
  onSave: (data: Partial<TSchedule>) => void;
  horses: TTrainerHorse[];
  tracks: Ttrack[];
  trainingTypes: TTrainingType[];
  loading: boolean;
  onBack: () => void;
  itemToEdit: TSchedule | null;
  dataLoading: boolean;
  dataConfig?: Partial<TSchedule>;
  configType?: "SIMPLE" | "ADVANCED";
  repportType: RepportType;
  onInputChange: (value: string) => void;
  horsesLoading: boolean;
};

export type ScheduleValues =
  | "FIXED_TIME"
  | "SYNCHRONISED_LAST_HOUR"
  | "LAST_WEEK";

const getBaseBest600 = () => {
  const userTz = dayjs.tz.guess();
  const highSpeedRegions = [
    "Australia",
    "New_Zealand",
    "Europe/London",
    "Europe/Dublin",
  ];

  const isHighSpeedRegion = highSpeedRegions.some((region) =>
    userTz.includes(region),
  );

  return isHighSpeedRegion ? 42 : 46;
};

function NewConfigStep3({
  repportType,
  configType,
  step,
  onSave,
  tracks,
  horses,
  trainingTypes,
  loading,
  onBack,
  itemToEdit,
  dataLoading,
  dataConfig,
  onInputChange,
  horsesLoading,
}: NewChannelStep1Props) {
  const BASE_VALUE = getBaseBest600();
  const DEFAULT_BEST600: number = BASE_VALUE;
  const MAX_SPLIT_DISTANCES: number[] = [800, 1000, 1200, 1400, 1600];
  const DEFAULT_MAX_SPLIT = 1600;
  const MAX_BEST_600 = 60;
  //Data State
  const [selectedTracks, setSelectedTracks] = useState<Ttrack[]>([]);
  const [selectedTypes, setSelectedTypes] = React.useState<TTrainingType[]>([]);
  const [selectedHorses, setSelectedHorses] = React.useState<THorse[]>([]);
  const [best600, setBest600] = useState<number>(DEFAULT_BEST600);
  const [maxSplitDistance, setMaxSplitDistance] = useState<number | null>(
    DEFAULT_MAX_SPLIT,
  );
  const [includePdf, setIncludePdf] = useState(false);
  const [mergePdf, setMergePdf] = useState(false);
  const [horse_ages, setSelectedAges] = React.useState<number[]>([]);
  const [useAllTrainings, setUseAllTrainings] = useState(true);

  //
  const [checkedRadio, setCheckedRadio] = useState<
    "separated" | "joined" | "none"
  >("separated");
  const [validationError, setValidationError] = useState<string | null>(null);
  const { auth } = useAuth();

  const renderSpeedTitle = useCallback(() => {
    let title = t("notification:field:showBest600");

    if (auth?.unit_system === "imperial") {
      title = t("notification:field:showBest600Imperial");
    }

    return title;
  }, [auth?.unit_system]);
  const renderAllTrainingsLabel = useCallback(() => {
    if (auth?.unit_system === "imperial") {
      return t("notification:field:allTrainingsImperial");
      // "All trainings with a main work distance of at least 2F and a main work average speed of at least 12.4 mph."
    }
    return t("notification:field:allTrainings");
    // "Includes all trainings with a main work distance of at least 400 m and a main work average speed of at least 20 km/h."
  }, [auth?.unit_system]);

  const renderValueAndUnit = useCallback(
    (v: number) => {
      let value =
        auth?.unit_system === "metric"
          ? v
          : formatMetricIntoImperialDistance(
              v ?? 0,
              auth.imperial_distance_miles_only,
            );
      let unit = auth?.unit_system === "metric" ? t("units:meters") : null;
      return compact([value, unit]).join(" ");
    },
    [auth?.unit_system],
  );

  useEffect(() => {
    if (!itemToEdit) {
      setSelectedTracks(
        [...tracks].filter((e) => dataConfig?.track_names?.includes(e["@id"])),
      );

      setSelectedHorses(
        [...horses]
          .filter((e) => dataConfig?.horses?.includes(e.horse["@id"]))
          .map((e) => e?.horse),
      );

      setSelectedTypes(
        [...trainingTypes].filter((e) =>
          dataConfig?.training_types?.includes(e["@id"]),
        ),
      );
      setBest600(dataConfig?.max_best600 ?? DEFAULT_BEST600);
      setMaxSplitDistance(dataConfig?.max_splits_distance ?? DEFAULT_MAX_SPLIT);

      let include_pdfs = dataConfig?.include_pdfs ?? true;
      let merge_pdfs = dataConfig?.merge_pdfs ?? false;

      setRadioValue(include_pdfs, merge_pdfs);

      setSelectedAges(dataConfig?.horse_ages ?? []);

      setIncludePdf(include_pdfs);
      setMergePdf(merge_pdfs);
      setUseAllTrainings(dataConfig?.all_generatable_trainings ?? true);
    }
  }, [dataConfig, itemToEdit]);

  useEffect(() => {
    if (!isNull(itemToEdit)) {
      setSelectedTracks(get(itemToEdit, "track_names", []) as Ttrack[]);

      setSelectedHorses(get(itemToEdit, "horses", []) as THorse[]);

      setSelectedTypes(
        get(itemToEdit, "training_types", []) as TTrainingType[],
      );

      // setSchedules(itemToEdit.schedules.map(s => ({ ...s, day_of_week: isNumber(s.day_of_week) ? [s.day_of_week] : s.day_of_week })))

      setBest600(itemToEdit?.max_best600);

      setMaxSplitDistance(itemToEdit?.max_splits_distance ?? DEFAULT_MAX_SPLIT);
      setSelectedAges(itemToEdit?.horse_ages ?? []);

      setIncludePdf(itemToEdit.include_pdfs);
      setMergePdf(itemToEdit.merge_pdfs);
      setRadioValue(itemToEdit.include_pdfs, itemToEdit.merge_pdfs);
      setUseAllTrainings(itemToEdit?.all_generatable_trainings ?? true);
    
    }
  }, [itemToEdit]);

  const _horses = useMemo(() => {
    return uniqBy(
      horses.map((e) => e.horse),
      "@id",
    );
  }, [horses]);

  useEffect(() => {
    if (configType === "SIMPLE") {
      handleRadioChange("separated");
    }
  }, [configType]);

  const dataObject: Partial<TSchedule> = {
    ...(configType === "ADVANCED"
      ? {
          horses: selectedHorses.map((e) => e?.["@id"]),
          track_names: selectedTracks.map((e) => e?.["@id"]),
          training_types: selectedTypes.map((e) => e?.["@id"]),
        }
      : {}),
    max_best600: best600,
    ...(repportType === "TRAINING_REPORT"
      ? {
          all_generatable_trainings: useAllTrainings,
        }
      : {}),
    ...(repportType === "TRAINING_REPORT"
      ? {
          include_pdfs: includePdf,
          merge_pdfs: mergePdf,
        }
      : {}),
    ...(repportType === "GALLOP_REPPORT"
      ? { max_splits_distance: maxSplitDistance }
      : {}),
    ...(repportType === "STABLE_REPPORT" ? { horse_ages } : {}),
  };

  const handleRadioChange = (key: "separated" | "joined" | "none") => {
    setCheckedRadio(key);
    switch (key) {
      case "separated":
        setIncludePdf(true);
        setMergePdf(false);
        break;
      case "joined":
        setIncludePdf(true);
        setMergePdf(true);
        break;
      case "none":
        setIncludePdf(false);
        setMergePdf(false);
        break;
      default:
        break;
    }
  };

  const setRadioValue = (include: boolean, merge: boolean) => {
    if (include && !merge) {
      setCheckedRadio("separated");
    }
    if (include && merge) {
      setCheckedRadio("joined");
    }
    if (!include && !merge) {
      setCheckedRadio("none");
    }
  };
  const handleInputChange = debounce(
    (value: string) => onInputChange(value),
    300,
  );

  const ages = Array.from({ length: 21 }, (_, i) => i);

  const handleAgeChange = (event: any) => {
    const {
      target: { value },
    } = event;

    setSelectedAges(
      typeof value === "string" ? value.split(",").map(Number) : value,
    );
  };

  const handleNext = () => {
    // Check if it's STABLE_REPPORT and if horses are less than 3
    if (
      auth.type === "GALLOP" &&
      repportType === "STABLE_REPPORT" &&
      !isEmpty(selectedHorses) &&
      selectedHorses.length < 3
    ) {
      setValidationError(t("validation:minHorsesRequired", { count: 3 }));
      return;
    }

    setValidationError(null);
    onSave(dataObject);
  };

  const getDefaultValue = useCallback(() => {
    let value =
      auth?.unit_system === "metric"
        ? 600
        : formatMetricIntoImperialDistance(
            600,
            auth.imperial_distance_miles_only,
          );
    let unit = auth?.unit_system === "metric" ? t("units:meters") : null;
    return compact([value, unit]).join(" ");
  }, [auth?.unit_system]);

  return (
    <>
      {dataLoading ? (
        <ListSkeleton height={50} length={5}></ListSkeleton>
      ) : (
        <>
          <Box mt={4} width={"100%"}>
            {configType === "ADVANCED" && (
              <>
                <Box my={2}>
                  <Typography mb={1} variant="subtitle2">
                    {t("editTrainingForm:trackName:label")}
                  </Typography>

                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      id="demo-simple-select"
                      options={tracks}
                      multiple
                      disableCloseOnSelect
                      isOptionEqualToValue={(option, value) =>
                        option["@id"] === value["@id"]
                      }
                      getOptionLabel={(option) => option.display_name ?? " - "}
                      getOptionKey={(option) => option.id}
                      value={selectedTracks}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t("editTrainingForm:trackName:label")}
                        />
                      )}
                      onChange={(_, values) => setSelectedTracks(values)}
                    ></Autocomplete>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(_, checked) => {
                          if (checked) {
                            setSelectedTracks([]);
                          }
                        }}
                        checked={selectedTracks.length === 0}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t("ownerSelection:selectAll")}
                      </Typography>
                    }
                  ></FormControlLabel>
                </Box>
                <Box my={2}>
                  <Typography mb={1} variant="subtitle2">
                    {t("editTrainingForm:trainingType:label")}
                  </Typography>

                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      id="demo-simple-select"
                      options={trainingTypes}
                      isOptionEqualToValue={(option, value) =>
                        option["@id"] === value["@id"]
                      }
                      multiple
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.display_name ?? " - "}
                      getOptionKey={(option) => option.id}
                      value={selectedTypes}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t("editTrainingForm:trainingType:label")}
                        />
                      )}
                      onChange={(_, values) => setSelectedTypes(values)}
                    ></Autocomplete>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(_, checked) => {
                          if (checked) {
                            setSelectedTypes([]);
                          }
                        }}
                        checked={selectedTypes.length === 0}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t("ownerSelection:selectAll")}
                      </Typography>
                    }
                  ></FormControlLabel>
                </Box>

                <Box my={2}>
                  <Typography mb={1} variant="subtitle2">
                    {t("horses")}
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Autocomplete
                      id="demo-simple-select"
                      options={_horses}
                      loading={horsesLoading}
                      multiple
                      onInputChange={(_, newInputValue) =>
                        handleInputChange(newInputValue)
                      }
                      onFocus={() => setValidationError(null)}
                      disableCloseOnSelect
                      getOptionLabel={(option) =>
                        option?.display_name ?? option?.name ?? " - "
                      }
                      isOptionEqualToValue={(option, value) =>
                        option["@id"] === value["@id"]
                      }
                      getOptionKey={(option) => option.id}
                      value={selectedHorses}
                      renderInput={(params) => (
                        <TextField
                          helperText={t("searchHorsesHint")}
                          {...params}
                          placeholder={t("horses")}
                        />
                      )}
                      onChange={(_, values) => {
                        setSelectedHorses(values);
                      }}
                    ></Autocomplete>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(_, checked) => {
                          if (checked) {
                            setSelectedHorses([]);
                          }
                        }}
                        checked={selectedHorses.length === 0}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t("ownerSelection:selectAll")}
                      </Typography>
                    }
                  ></FormControlLabel>
                </Box>
                {repportType === "STABLE_REPPORT" && (
                  <Box my={2}>
                    <Typography mb={1} variant="subtitle2">
                      {t("age")}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        labelId="horse-age-label"
                        multiple
                        value={horse_ages}
                        onChange={handleAgeChange}
                        placeholder={t("notification:field:showAges")}
                        renderValue={(selected) => selected.join(", ")}
                      >
                        {ages.map((age) => (
                          <MenuItem key={age} value={age}>
                            <Checkbox checked={horse_ages.indexOf(age) > -1} />
                            <ListItemText primary={`${age}`} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={horse_ages.length === 0}
                          onChange={(_, checked) => {
                            if (checked) {
                              setSelectedAges([]);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {t("ownerSelection:selectAll")}
                        </Typography>
                      }
                    ></FormControlLabel>
                  </Box>
                )}
              </>
            )}

            {configType === "SIMPLE" && (
              <Alert variant="outlined" color="info">
                {repportType === "STABLE_REPPORT"
                  ? t("configuration:buildSimpleStableHint", {
                      distance: renderValueAndUnit(600),
                    })
                  : t("configuration:buildSimpleHint")}
              </Alert>
            )}
            {repportType === "TRAINING_REPORT" ? (
              <Box display={"flex"} flexDirection={"column"} gap={1} my={2}>
                <Box display="flex" flexDirection="column" gap={1}>
                  {/* Option 1 — All trainings */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useAllTrainings}
                        onChange={() => setUseAllTrainings(true)}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {t("notification:field:allTrainingsTitle")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {renderAllTrainingsLabel()}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", mr: 0 }}
                  />

                  {/* Option 2 — Best 600 filter */}
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!useAllTrainings}
                          onChange={() => setUseAllTrainings(false)}
                          size="small"
                        />
                      }
                      label={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color={
                              useAllTrainings ? "text.disabled" : "text.primary"
                            }
                          >
                            {renderSpeedTitle()}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "center", mr: 0, flex: 1 }}
                    />
                    <Typography
                      variant="caption"
                      color={
                        useAllTrainings ? "text.disabled" : "text.secondary"
                      }
                    >
                      {best600} / {MAX_BEST_600}
                    </Typography>
                  </Box>

                  {/* Slider — tied to option 2 */}
                  <Box px={1}>
                    <Slider
                      min={31}
                      max={MAX_BEST_600}
                      step={0.1}
                      valueLabelDisplay="auto"
                      value={best600}
                      disabled={useAllTrainings}
                      onChange={(_, value) => {
                        setBest600(value as number);
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box display={"flex"} flexDirection={"column"} gap={1} my={2}>
                <Box
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {renderSpeedTitle()}
                  </Typography>
                  <Typography variant="caption">
                    {best600} / {MAX_BEST_600}
                  </Typography>
                </Box>
                <Slider
                  min={31}
                  max={MAX_BEST_600}
                  step={0.1}
                  valueLabelDisplay="auto"
                  value={best600}
                  onChange={(_, value) => {
                    setBest600(value as number);
                  }}
                ></Slider>
                {repportType === "GALLOP_REPPORT" && (
                  <>
                    <Typography variant="body2" fontWeight={500}>
                      {t("maxSplit:label", {
                        maxSplitDistance: renderValueAndUnit(
                          maxSplitDistance ?? 0,
                        ),
                      })}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={maxSplitDistance}
                        onChange={(e) =>
                          setMaxSplitDistance(
                            e.target.value ? toNumber(e.target.value) : null,
                          )
                        }
                      >
                        {MAX_SPLIT_DISTANCES.map((distance) => (
                          <MenuItem value={distance} key={distance}>
                            {auth?.unit_system === "metric"
                              ? distance
                              : formatMetricIntoImperialDistance(
                                  distance,
                                  auth.imperial_distance_miles_only,
                                )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Box>
            )}
            {configType === "ADVANCED" && repportType === "TRAINING_REPORT" && (
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {t("configuration:attachements")}
                </Typography>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    value={checkedRadio}
                    name="radio-buttons-group"
                    onChange={(_, value) =>
                      handleRadioChange(
                        value as "separated" | "joined" | "none",
                      )
                    }
                  >
                    <FormControlLabel
                      value="separated"
                      control={<Radio />}
                      label={
                        <Typography variant="subtitle2">
                          {t("configuration:attachements:separated")}
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="joined"
                      control={<Radio />}
                      label={
                        <Typography variant="subtitle2">
                          {t("configuration:attachements:joined")}
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 3 }}></Divider>
          <Box display={"flex"} flexDirection={"column"}>
            {validationError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {validationError}
              </Alert>
            )}
            <LoadingButton
              loading={loading}
              sx={{ alignSelf: "end" }}
              onClick={handleNext}
              variant="contained"
            >
              {t("button:next")}
            </LoadingButton>
          </Box>
        </>
      )}
    </>
  );
}

export default NewConfigStep3;
